import { StateCreator } from 'zustand';

import { AnalyticState } from './types/AnalyticState';
import { AnalyticStateDependencies } from './types/AnalyticStateDependencies';
import { ApplicationSubmittingAnalyticsParams } from './types/ApplicationSubmittingAnalyticsParams';
import { RouteChangeAnalyticsParams } from './types/RouteChangeAnalyticsParams';
import { SignUpVariantAssignedAnalyticsParams } from './types/SignUpVariantAssignedAnalyticsParams';

export const analyticStore =
  ({ analytics }: AnalyticStateDependencies): StateCreator<AnalyticState> =>
  (set, get) => ({
    inputFocusedTimestamp: Date.now(),

    bindElementAnalyticsListeners: (): void => {
      const { sendElementAnalytics } = get();

      document.addEventListener('focusin', sendElementAnalytics);
      document.addEventListener('focusout', sendElementAnalytics);
      document.addEventListener('click', sendElementAnalytics);
    },
    clearElementAnalyticsListeners: (): void => {
      const { sendElementAnalytics } = get();

      document.removeEventListener('focusin', sendElementAnalytics);
      document.removeEventListener('focusout', sendElementAnalytics);
      document.removeEventListener('click', sendElementAnalytics);
    },
    sendApplicationFailedImportAnalytics: (): void => {
      analytics.track('application-failed-import');
    },
    sendApplicationSubmittedAnalytics: (): void => {
      analytics.track('application-submitted');
    },
    sendApplicationSubmittingAnalytics: (
      params: ApplicationSubmittingAnalyticsParams,
    ): void => {
      const { email, firstName, lastName, phone, zipCode } = params;

      analytics.track('application-submitting', {
        email,
        firstName,
        lastName,
        phone,
        zipCode,
      });
    },
    sendApplicationTaskButtonClickedAnalytics: (
      applicationStatus: string,
      buttonName: string,
    ): void => {
      analytics.track('ApplicationTaskButtonClicked', {
        ApplicationStatus: applicationStatus,
        Button: buttonName,
      });
    },
    sendApplicationViewedAnalytics: (applicationStatus: string): void => {
      analytics.track('ApplicationViewed', {
        ApplicationStatus: applicationStatus,
      });
    },
    sendElementAnalytics: (event: FocusEvent | MouseEvent): void => {
      const target: EventTarget = event.target;

      if (!(target instanceof HTMLElement)) {
        return;
      }

      const isInput = target instanceof HTMLInputElement;
      const isButton = target instanceof HTMLButtonElement;

      // determine whether the element was focused or blurred
      const isFocused = event.type === 'focusin';
      const isBlurred = event.type === 'focusout';
      const isClick = event.type === 'click';

      if (
        (!isInput && !isButton) || // element is not an input or button
        (isInput && isClick) || // element is an input that was clicked (only capture input focus/blur)
        (isButton && !isClick) // element is an button that was not clicked (only capture button clicks)
      ) {
        return;
      }

      // assign event data default values
      let eventName = '';
      let elementName = '';
      let elementLocation = '';
      let eventDuration = null;

      const pathname = window.location.pathname;

      // focused input data
      if (isInput && isFocused) {
        eventName = 'Input In';
        elementName = target.name || '';
        elementLocation = `${pathname}__${elementName}`;

        // store timestamp of when input was focused for blurred calculation
        set({ inputFocusedTimestamp: Date.now() });
      }

      // blurred input data
      if (isInput && isBlurred) {
        eventName = 'Input Out';
        elementName = target.name || '';
        elementLocation = `${pathname}__${elementName}`;

        // calculate the amount of time that the input was focused for
        eventDuration = (Date.now() - get().inputFocusedTimestamp) / 1000;
      }

      // clicked button data
      if (isButton && isClick) {
        eventName = 'Button';
        elementName = target.innerText || '';
        elementLocation = pathname;
      }

      analytics.track(eventName, {
        [eventName]: elementName,
        ...(eventDuration ? { 'Input Duration': eventDuration } : {}),
        Location: elementLocation,
      });
    },
    sendEmbeddedInsuranceVerificationRedirectAnalytics: (
      autopayNumber: string,
    ): void => {
      analytics.track('Embedded Insurance Redirected', {
        autopayNumber,
      });
    },
    sendLandedWithParametersAnalytics: (
      url: string,
      urlHost: string,
      urlPathname: string,
      parameters: Record<string, unknown>,
    ): void => {
      analytics.track('LandedWithParameters', {
        url,
        urlHost,
        urlPathname,
        ...parameters,
      });
    },
    sendLoanDocumentAnalytics: (documentType: string, count: number): void => {
      analytics.track('submitted-protection-document', {
        Count: count,
        LoanDocumentType: documentType,
      });
    },
    sendMailCodeErrorAnalytics: (): void => {
      analytics.track('mail-code-error');
    },
    sendMailCodeFoundAnalytics: (): void => {
      analytics.track('mail-code-found');
    },
    sendNewApplicationAnalytics: (): void => {
      analytics.track('application-new-app');
    },
    sendNewDashboardApplicationAnalytics: (): void => {
      analytics.track('application-new-dashboard');
    },
    sendRouteChangeAnalytics: (params: RouteChangeAnalyticsParams): void => {
      const {
        fromRouteUrl,
        fromRouteUrlTrimmed,
        toRouteUrl,
        toRouteUrlTrimmed,
      } = params;

      analytics.track('route-change', {
        fromRouteUrl,
        fromRouteUrlTrimmed,
        toRouteUrl,
        toRouteUrlTrimmed,
      });
    },
    sendSignUpVariantAssignedAnalytics: (
      params: SignUpVariantAssignedAnalyticsParams,
    ): void => {
      analytics.track('sign-up-variant-assigned', params);
    },
    sendUserActivatedAccountAnalytics: (): void => {
      analytics.track('user-activated-account');
    },
    sendUserActivatedApplicationAnalytics: (): void => {
      analytics.track('user-activated-application');
    },
    sendUserActivatingAccountAnalytics: (): void => {
      analytics.track('user-activating-account');
    },
    sendUserActivatingApplicationAnalytics: (): void => {
      analytics.track('user-activating-application');
    },
    sendUserAuthenticatedAnalytics: (): void => {
      analytics.track('user-authenticated');
    },
    sendUserAuthenticatingAnalytics: (): void => {
      analytics.track('user-authenticating');
    },
    sendUserRegisteredAnalytics: (): void => {
      analytics.track('user-registered');
    },
    sendUserRegisteringAnalytics: (): void => {
      analytics.track('user-registering');
    },
    sendVehicleDuplicateErrorAnalytics: (): void => {
      analytics.track('vehicle-duplicate-error');
    },
    sendVehicleDuplicateUpdateAnalytics: (): void => {
      analytics.track('vehicle-duplicate-update');
    },
    sendVehicleRegisteredAnalytics: (): void => {
      analytics.track('vehicle-registered');
    },
    sendVehicleSelectionFoundAnalytics: (): void => {
      analytics.track('vehicle-selection-found');
    },
    sendVehicleSelectionMissingAnalytics: (): void => {
      analytics.track('vehicle-selection-missing');
    },
    sendVerificationCompletedAnalytics: (): void => {
      analytics.track('verification-completed');
    },
    sendVerificationUploadedAnalytics: (documentType: string): void => {
      analytics.track('verification-uploaded', {
        DocumentType: documentType,
      });
    },
  });
